import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ShieldCheck, Timer, Plus, Info, ClockCounterClockwise, DownloadSimple, Archive, Prohibit, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

// API returns [{ip, description}]; tolerate legacy strings.
const fromApi = (arr) => (arr || []).map((e) =>
  typeof e === "string" ? { ip: e, description: "" } : { ip: e.ip || "", description: e.description || "" }
);

export default function SecuritySettings() {
  const [cfg, setCfg] = useState(null);
  const [ipList, setIpList] = useState([]);
  const [myIp, setMyIp] = useState("");
  const [saving, setSaving] = useState(false);
  const [archives, setArchives] = useState([]);
  const [blocked, setBlocked] = useState([]);

  const loadArchives = () => api.get("/users/audit/archives").then(r => setArchives(r.data)).catch(() => {});
  const loadBlocked = () => api.get("/security/blocked-signins").then(r => setBlocked(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/security/settings").then(r => { setCfg(r.data); setIpList(fromApi(r.data.ip_allowlist)); });
    api.get("/security/my-ip").then(r => setMyIp(r.data.ip)).catch(() => {});
    loadArchives();
    loadBlocked();
  }, []);

  const downloadArchive = async (aid) => {
    try {
      const token = localStorage.getItem("helix_token");
      const res = await fetch(`${API}/users/audit/archives/${aid}/download`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a"); a.href = url; a.download = `audit-archive-${aid.slice(0, 8)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  if (!cfg) return <div className="p-6 text-muted-foreground">Loading…</div>;

  const save = async () => {
    // Every non-empty IP row must carry a description before saving.
    const rows = ipList.map(r => ({ ip: (r.ip || "").trim(), description: (r.description || "").trim() })).filter(r => r.ip);
    const missing = rows.find(r => !r.description);
    if (missing) { toast.error(`Add a description for "${missing.ip}"`); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/security/settings", {
        idle_timeout_minutes: Number(cfg.idle_timeout_minutes) || 0,
        ip_allowlist_enabled: cfg.ip_allowlist_enabled,
        ip_allowlist: rows,
        ip_allowlist_exempt_emails: cfg.ip_allowlist_exempt_emails || [],
        audit_retention_days: Number(cfg.audit_retention_days) || 0,
        audit_archive_enabled: !!cfg.audit_archive_enabled,
      });
      setCfg(data); setIpList(fromApi(data.ip_allowlist));
      toast.success("Security settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const setRow = (i, field, val) => setIpList(ipList.map((r, idx) => idx === i ? { ...r, [field]: field === "description" ? val.slice(0, 120) : val } : r));
  const addRow = () => setIpList([...ipList, { ip: "", description: "" }]);
  const removeRow = (i) => setIpList(ipList.filter((_, idx) => idx !== i));

  const addMyIp = () => {
    if (!myIp) return;
    if (ipList.some(r => (r.ip || "").trim() === myIp)) return;
    setIpList([...ipList, { ip: myIp, description: "" }]);
  };

  return (
    <div className="p-6 max-w-4xl" data-testid="security-view">
      <div className="mb-6">
        <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-1">Administration</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black flex items-center gap-3"><ShieldCheck size={30} className="text-primary" weight="fill" /> Security &amp; Access</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Configure idle auto-logoff and restrict logins to trusted networks.</p>
      </div>

      <Card className="p-6 border-border rounded-sm mb-6 space-y-4" data-testid="idle-card">
        <div className="flex items-center gap-2"><Timer size={16} className="text-primary" /><span className="text-[11px] font-mono uppercase tracking-widest text-primary">Idle Auto-Logoff</span></div>
        <p className="text-xs text-muted-foreground">Automatically sign out <strong>agents &amp; admins</strong> after a period of inactivity. Set to 0 to disable.</p>
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-[10px] font-mono uppercase text-muted-foreground">Idle timeout (minutes)</Label>
            <Input type="number" min="0" data-testid="idle-timeout-input" className="mt-1 rounded-sm font-mono w-40" value={cfg.idle_timeout_minutes} onChange={(e) => setCfg({ ...cfg, idle_timeout_minutes: e.target.value })} />
          </div>
          <span className="text-xs text-muted-foreground pb-2.5">{Number(cfg.idle_timeout_minutes) > 0 ? `Sessions end after ${cfg.idle_timeout_minutes} min idle` : "Auto-logoff disabled"}</span>
        </div>
      </Card>

      <Card className="p-6 border-border rounded-sm mb-6 space-y-4" data-testid="ip-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><ShieldCheck size={16} className="text-primary" /><span className="text-[11px] font-mono uppercase tracking-widest text-primary">IP Allowlist</span></div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono text-muted-foreground">{cfg.ip_allowlist_enabled ? "Enabled" : "Disabled"}</span>
            <Switch data-testid="ip-enabled-switch" checked={cfg.ip_allowlist_enabled} onCheckedChange={(v) => setCfg({ ...cfg, ip_allowlist_enabled: v })} />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">When enabled, only logins from these IP addresses / CIDR ranges are allowed (applies to all users). Leaving the list empty allows all IPs even when enabled.</p>
        <div className="text-[11px] font-mono text-muted-foreground flex items-center gap-2 flex-wrap">
          <Info size={12} /> Your current IP is <span className="text-primary" data-testid="my-ip">{myIp || "unknown"}</span>
          <button data-testid="add-my-ip" onClick={addMyIp} className="inline-flex items-center gap-1 text-primary hover:underline"><Plus size={11} /> add to list</button>
        </div>
        <div>
          <Label className="text-[10px] font-mono uppercase text-muted-foreground">Allowed IPs / CIDR</Label>
          <p className="text-[11px] text-muted-foreground mt-0.5 mb-2">A description is required for every entry — note why the IP is trusted (e.g. "HQ office", "VPN gateway").</p>
          {ipList.length === 0 ? (
            <div className="text-xs text-muted-foreground border border-dashed border-border rounded-sm px-3 py-4 text-center" data-testid="ip-allowlist-empty">No IPs added. All IPs are allowed.</div>
          ) : (
            <div className="space-y-2" data-testid="ip-allowlist-rows">
              {ipList.map((row, i) => (
                <div key={i} className="flex items-start gap-2" data-testid={`ip-row-${i}`}>
                  <Input
                    data-testid={`ip-input-${i}`}
                    className="rounded-sm font-mono text-xs w-48 shrink-0"
                    placeholder="203.0.113.5 or 10.0.0.0/24"
                    value={row.ip}
                    onChange={(e) => setRow(i, "ip", e.target.value)}
                  />
                  <div className="flex-1">
                    <Input
                      data-testid={`ip-desc-${i}`}
                      className={`rounded-sm text-xs ${row.ip.trim() && !row.description.trim() ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                      placeholder="Description (why is this IP trusted?)"
                      maxLength={120}
                      value={row.description}
                      onChange={(e) => setRow(i, "description", e.target.value)}
                    />
                    {row.ip.trim() && !row.description.trim() && (
                      <span className="text-[10px] text-red-600 dark:text-red-400" data-testid={`ip-desc-error-${i}`}>Description required</span>
                    )}
                  </div>
                  <Button type="button" variant="ghost" size="icon" data-testid={`ip-remove-${i}`} onClick={() => removeRow(i)} className="rounded-sm text-muted-foreground hover:text-red-600 shrink-0"><Trash size={15} /></Button>
                </div>
              ))}
            </div>
          )}
          <button type="button" data-testid="ip-add-row" onClick={addRow} className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline font-mono"><Plus size={12} /> Add IP address</button>
        </div>
        {(cfg.ip_allowlist_exempt_emails || []).length > 0 && (
          <div className="border border-border rounded-sm px-3 py-2.5" data-testid="ip-exempt-list">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5 flex items-center gap-1"><ShieldCheck size={12} className="text-primary" /> Always allowed (bypass IP restriction)</div>
            <div className="flex flex-wrap gap-1.5">
              {(cfg.ip_allowlist_exempt_emails || []).map((e) => (
                <span key={e} data-testid={`ip-exempt-${e}`} className="text-[11px] font-mono px-2 py-0.5 rounded-sm bg-primary/10 text-primary border border-primary/30">{e}</span>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1.5">Break-glass admins can always sign in regardless of network, so admins are never locked out.</p>
          </div>
        )}
        {cfg.ip_allowlist_enabled && (
          <div className="text-[11px] font-mono text-amber-700 dark:text-amber-400 flex items-center gap-1"><Info size={12} /> Make sure your current IP is included, or you may be locked out on next login.</div>
        )}
      </Card>

      <Card className="p-6 border-border rounded-sm mb-6 space-y-4" data-testid="blocked-signins-card">
        <div className="flex items-center gap-2"><Prohibit size={16} className="text-red-600 dark:text-red-400" /><span className="text-[11px] font-mono uppercase tracking-widest text-primary">Blocked Sign-ins</span></div>
        <p className="text-xs text-muted-foreground">Sign-in attempts denied by the IP allowlist — who tried, from where, and when. Use this to spot lockouts or unexpected access attempts.</p>
        {blocked.length === 0 ? (
          <div className="text-xs text-muted-foreground border border-border rounded-sm px-3 py-4 text-center" data-testid="blocked-empty">No IP-blocked sign-ins recorded.</div>
        ) : (
          <div className="border border-border rounded-sm overflow-hidden" data-testid="blocked-list">
            <div className="grid grid-cols-[1.6fr_1fr_1.4fr] gap-2 px-3 py-2 bg-muted/40 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              <span>Account</span><span>IP Address</span><span>When</span>
            </div>
            <div className="divide-y divide-border max-h-72 overflow-y-auto">
              {blocked.map((b, i) => (
                <div key={i} className="grid grid-cols-[1.6fr_1fr_1.4fr] gap-2 px-3 py-2 text-xs items-center" data-testid={`blocked-row-${i}`}>
                  <span className="truncate" title={b.email || "unknown"}>{b.email || <span className="text-muted-foreground">(unknown)</span>}</span>
                  <span className="font-mono text-red-600 dark:text-red-400">{b.ip || "—"}</span>
                  <span className="font-mono text-muted-foreground">{b.at ? new Date(b.at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) + " IST" : "—"}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      <Card className="p-6 border-border rounded-sm mb-6 space-y-4" data-testid="retention-card">
        <div className="flex items-center gap-2"><ClockCounterClockwise size={16} className="text-primary" /><span className="text-[11px] font-mono uppercase tracking-widest text-primary">Audit Retention</span></div>
        <p className="text-xs text-muted-foreground">How long to keep the <strong>user change &amp; login history</strong> (role/skills/timesheet changes, logins, and failed sign-ins). Older records are pruned automatically to keep the trail fast. Set to <strong>0</strong> to keep forever.</p>
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-[10px] font-mono uppercase text-muted-foreground">Retention (days)</Label>
            <Input type="number" min="0" max="3650" data-testid="retention-days-input" className="mt-1 rounded-sm font-mono w-40" value={cfg.audit_retention_days} onChange={(e) => setCfg({ ...cfg, audit_retention_days: e.target.value })} />
          </div>
          <span className="text-xs text-muted-foreground pb-2.5">{Number(cfg.audit_retention_days) > 0 ? `History older than ${cfg.audit_retention_days} days is removed` : "History kept forever"}</span>
        </div>
        <label className="flex items-center justify-between gap-2 text-sm border border-border rounded-sm px-3 py-2">
          <span className="flex items-center gap-2"><Archive size={15} className="text-primary" /> Archive history to a downloadable CSV before deleting (compliance)</span>
          <Switch data-testid="archive-enabled-switch" checked={!!cfg.audit_archive_enabled} onCheckedChange={(v) => setCfg({ ...cfg, audit_archive_enabled: v })} />
        </label>
        {archives.length > 0 && (
          <div className="border border-border rounded-sm divide-y divide-border" data-testid="archive-list">
            <div className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Saved archives</div>
            {archives.map((a) => (
              <div key={a.id} className="flex items-center gap-3 px-3 py-2 text-xs" data-testid={`archive-row-${a.id}`}>
                <span className="font-mono text-muted-foreground">{(a.created_at || "").slice(0, 10)}</span>
                <span className="text-muted-foreground">{a.count} events · {(a.period_from || "").slice(0, 10)} → {(a.period_to || "").slice(0, 10)}</span>
                <button onClick={() => downloadArchive(a.id)} data-testid={`archive-download-${a.id}`} className="ml-auto inline-flex items-center gap-1 text-primary hover:underline"><DownloadSimple size={13} /> Download</button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Button data-testid="security-save" onClick={save} disabled={saving} className="rounded-sm font-mono uppercase text-xs">{saving ? "Saving…" : "Save Security Settings"}</Button>
    </div>
  );
}
